import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, AuthRequiredError } from "../lib/api";

interface BalanceInfo {
  hours_remaining: number;
  topup_rate: number;
  has_subscription: boolean;
  subscription?: {
    tier_name: string;
  };
}

interface Props {
  minutesNeeded: number;
  minutesRemaining: number;
  message?: string;
  onClose: () => void;
  onProceedAnyway?: () => void;
  isGuest?: boolean;
  isUnverifiedUser?: boolean;
}

// Quick top-up options in minutes
const TOPUP_OPTIONS = [30, 60, 120, 300];

function formatPrice(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

export default function InsufficientBalanceModal({
  minutesNeeded,
  minutesRemaining,
  message,
  onClose,
  onProceedAnyway,
  isGuest = false,
  isUnverifiedUser = false,
}: Props) {
  const [balanceInfo, setBalanceInfo] = useState<BalanceInfo | null>(null);
  const [buyingMinutes, setBuyingMinutes] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuestUser, setIsGuestUser] = useState(isGuest);
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isGuest) {
      // Skip API call for guests
      setBalanceInfo({
        hours_remaining: minutesRemaining / 60,
        topup_rate: 0.90,
        has_subscription: false,
      });
      setLoading(false);
      return;
    }
    
    apiFetch<BalanceInfo>("/billing/v2/balance")
      .then(setBalanceInfo)
      .catch((err) => {
        // If auth error, treat as guest
        if (err instanceof AuthRequiredError || err?.name === "AuthRequiredError") {
          setIsGuestUser(true);
        }
        // Fallback for users without subscription
        setBalanceInfo({
          hours_remaining: minutesRemaining / 60,
          topup_rate: 0.90, // PAYG rate
          has_subscription: false,
        });
      })
      .finally(() => setLoading(false));
  }, [minutesRemaining, isGuest]);

  const topupRate = balanceInfo?.topup_rate ?? 0.90;
  const hasSubscription = balanceInfo?.has_subscription ?? false;
  const tierName = balanceInfo?.subscription?.tier_name;

  async function handleBuyMinutes(minutes: number) {
    setBuyingMinutes(minutes);
    const priceCents = Math.round((minutes / 60) * topupRate * 100);
    
    try {
      const { checkout_url } = await apiFetch<{ checkout_url: string }>(
        "/billing/v2/payg-checkout",
        {
          method: "POST",
          body: JSON.stringify({
            payg_minutes: minutes,
            price_cents: priceCents,
            success_url: window.location.origin + "/billing/success",
            cancel_url: window.location.href,
          }),
        },
      );
      window.location.href = checkout_url;
    } catch {
      setBuyingMinutes(null);
    }
  }

  async function handleResendVerification() {
    setIsResending(true);
    setResendStatus('idle');
    try {
      await apiFetch('/auth/resend-verification', { method: 'POST' });
      setResendStatus('success');
    } catch {
      setResendStatus('error');
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">
          Need more minutes
        </h2>
        
        <p className="mt-2 text-sm text-gray-500">
          {message ? (
            message
          ) : (
            <>
              You have <span className="font-medium text-gray-900">{minutesRemaining}</span> minutes remaining.
              {minutesNeeded > 0 && (
                <> You need approximately <span className="font-medium text-gray-900">{minutesNeeded}</span> more.</>
              )}
            </>
          )}
        </p>

        {/* Guest prompt */}
        {isGuestUser ? (
          <div className="mt-5 space-y-4">
            <div className="rounded-lg bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                <strong>Guest trial ended!</strong> Sign up for full early access — 20 hours of transcription and unlimited AI chat, free while we're in testing. <a href="/terms#early-access" className="underline">Fair use terms</a> apply.
              </p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/signup"
                onClick={onClose}
                className={`rounded-lg bg-blue-600 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-700 ${minutesRemaining > 0 ? 'flex-1' : 'flex-1'}`}
              >
                Sign up free
              </Link>
              {minutesRemaining > 0 && (
                <button
                  onClick={() => {
                    onClose();
                    onProceedAnyway?.();
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Transcribe anyway
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {isUnverifiedUser && !isGuestUser && (
              <div className="mt-4 rounded-lg bg-blue-50 p-4 border border-blue-100">
                <p className="text-sm italic text-blue-900">
                  We're in beta — verify your email to unlock 20h free! 🤑 Don't forget to check your spam folder.
                </p>
                {resendStatus === 'success' ? (
                  <p className="mt-2 text-sm font-medium text-green-700">
                    ✓ Verification email sent! Check your inbox (and spam folder).
                  </p>
                ) : resendStatus === 'error' ? (
                  <p className="mt-2 text-sm font-medium text-red-700">
                    Failed to resend. Please try again later.
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={isResending}
                    className="mt-2 inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {isResending ? 'Sending…' : 'Resend verification email'}
                  </button>
                )}
              </div>
            )}

            {/* Subscription badge */}
            {hasSubscription && tierName && (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                {tierName} subscriber — {formatPrice(topupRate * 100)}/hr top-up rate
              </div>
            )}

            {/* Quick top-up options */}
            {!loading && (
              <div className="mt-5 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Quick top-up
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {TOPUP_OPTIONS.map((mins) => {
                    const hours = mins / 60;
                    const priceCents = Math.round(hours * topupRate * 100);
                    return (
                      <button
                        key={mins}
                        onClick={() => handleBuyMinutes(mins)}
                        disabled={buyingMinutes !== null}
                        className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-left hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {mins >= 60 ? `${hours} hr${hours > 1 ? 's' : ''}` : `${mins} min`}
                        </span>
                        <span className="text-sm font-semibold text-blue-600">
                          {buyingMinutes === mins ? "..." : formatPrice(priceCents)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className={`mt-5 flex items-center border-t border-gray-100 pt-4 ${isGuestUser ? 'justify-end' : 'justify-between'}`}>
          {!isGuestUser && (
            <Link
              to="/pricing"
              onClick={onClose}
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              {hasSubscription ? "Upgrade plan" : "View subscription plans"} →
            </Link>
          )}
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
